import { Link } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react';
import { usePagination } from '../../contexts/PaginationContext';
import { useNotification } from '../../contexts/NotificationContext';
import PaginationControls from '../../components/PaginationControls';
import Footer from '../../components/Footer';
import { authFetch } from '../../utils/authFetch';
import 'bootstrap-icons/font/bootstrap-icons.css';

const FuncoesTable = ({ funcoes, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table align-items-center mb-0">
        <thead>
          <tr>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
              Nome</th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
              Tipo</th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
              Ações</th>
          </tr>
        </thead>
        <tbody>
          {funcoes.map((funcao) => (
            <tr key={funcao.id}>
              <td className="text-xs ps-2">{funcao.name}</td>
              <td className="text-xs ps-2">
                {funcao.jobPositionType === 0
                  ? 'Mensalista'
                  : funcao.jobPositionType === 1
                    ? 'Horista'
                    : '-'
                }
              </td>
              <td className="text-xs ps-2">
                <Link 
                  to={`/Funcoes/Editar/${funcao.id}`} 
                  className="me-3"
                  title="Editar"
                >
                  <i className="bi bi-pencil-square text-primary"></i>
                </Link>
                <Link 
                  to={`/Funcoes/Excluir/${funcao.id}`}
                  className="me-3"
                  title="Excluir"
                >
                  <i className="bi bi-trash text-danger"></i>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Funcoes = () => {
  const [funcoes, setFuncoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const isFirstRequest = useRef(true);

  const { showNotification } = useNotification();

  const {
    page,
    pageSize,
    setTotalElements,
    setTotalPages,
    setPage,
  } = usePagination();

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (value.length >= 3 || value.length === 0) {
      const timeoutId = setTimeout(() => {
        isFirstRequest.current = true; // Reset para primeira requisição na busca
        fetchFuncoes(value);
      }, 500);
      setSearchTimeout(timeoutId);
    }
  };

  const fetchFuncoes = async (name = searchTerm) => {
    try {
      setLoading(true);
      const currentPage = isFirstRequest.current ? 0 : page;
      const queryParams = new URLSearchParams();
      if (name && name.length >= 3) {
        queryParams.append('name', name);
      }
      
      const url = `job-position/${currentPage}/${pageSize}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error("Erro ao buscar Funções");
      }

      const data = await response.json();
      setFuncoes(data.content);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      
      // Atualiza a página após o request
      setPage(currentPage);
      isFirstRequest.current = false;
    } catch (err) {
      setError(err.message);
      showNotification("danger", "Erro ao carregar funções");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFuncoes();
  }, [page, pageSize]);

  if (error) return <p>Erro: {error}</p>;

  return (
    <div className="content">
      <div className="text-end mb-3">
        <Link to={'Novo'} className='btn btn-info btn-sm'>Cadastrar Função</Link>
      </div>
      <h3 className="font-weight-bolder mb-3">Funções</h3>
      <div className="card">
        <div className="card-body p-3">
          <div className="d-flex justify-content-end mb-3">
            <div className="input-group" style={{ maxWidth: '300px' }}>
              <span className="input-group-text border-0 bg-transparent">
                <i className="bi bi-search text-secondary"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>

          <FuncoesTable 
            funcoes={funcoes}
            loading={loading}
          />

          <PaginationControls currentCount={funcoes.length} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Funcoes;
